import type { ReactElement } from "react";
import React, { createContext, useCallback } from "react";
import { NativeScrollEvent, NativeSyntheticEvent } from "react-native";
import type { TamaguiElement, ViewProps } from "tamagui";
import { View } from "tamagui";

const THUMB_RATIO = 0.2;

const useScrollbar = (horizontal: boolean) => {
  const [thumbLength, setThumbLength] = React.useState(0);
  const [trackLength, setTrackLength] = React.useState(0);

  const thumbRef = React.useRef<TamaguiElement>(null);
  const initialized = React.useRef(false);

  const onScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentSize, layoutMeasurement, contentOffset } = e.nativeEvent;

      const visibleWidth = layoutMeasurement.width;
      const visibleHeight = layoutMeasurement.height;
      const contentWidth = contentSize.width;
      const contentHeight = contentSize.height;
      const scrollOffsetX = contentOffset.x;
      const scrollOffsetY = contentOffset.y;

      const scrollbarSize = horizontal
        ? visibleWidth * THUMB_RATIO
        : visibleHeight * THUMB_RATIO;

      const scrollbarPositionInPercentage = Math.round(
        (horizontal
          ? scrollOffsetX / (contentWidth - visibleWidth)
          : scrollOffsetY / (contentHeight - visibleHeight)) * 100,
      );

      const pixelPerPercent =
        (horizontal
          ? visibleWidth - scrollbarSize
          : visibleHeight - scrollbarSize) / 100;

      const pixelOffset = pixelPerPercent * scrollbarPositionInPercentage;

      if (thumbRef.current) {
        const node = thumbRef.current as HTMLElement;
        node.style.transform = horizontal
          ? `translateX(${pixelOffset}px)`
          : `translateY(${pixelOffset}px)`;
      }

      if (!initialized.current) {
        initialized.current = true;
        setTrackLength(horizontal ? visibleWidth : visibleHeight);
        setThumbLength(scrollbarSize);
      }
    },
    [horizontal],
  );

  return {
    onScroll,
    thumbLength,
    trackLength,
    thumbRef,
  };
};

interface ScrollbarWrapperContext {
  horizontal: boolean;
  thumbLength: number;
  trackLength: number;
  thumbRef: React.RefObject<TamaguiElement> | null;
}

const ScrollbarWrapperContext = createContext<ScrollbarWrapperContext>({
  horizontal: false,
  thumbLength: 0,
  trackLength: 0,
  thumbRef: null,
});

const useScrollbarContext = () => {
  const context = React.useContext(ScrollbarWrapperContext);
  if (!context) {
    throw new Error(
      "useScrollbarContext must be used within a ScrollbarWrapperContext.Provider",
    );
  }
  return context;
};

const hasDisplayName = (type: unknown): type is { displayName?: string } => {
  return (
    (typeof type === "function" || typeof type === "object") &&
    type !== null &&
    "displayName" in type
  );
};

const getDisplayName = (
  child: ReactElement<unknown, string | React.JSXElementConstructor<any>>,
): string | undefined => {
  const componentType = child.type;
  if (hasDisplayName(componentType)) {
    return componentType.displayName;
  }
  return undefined;
};

const EnhancedChildren = React.memo(
  ({
    children,
    opt,
  }: {
    children: ReactElement[];
    opt: {
      horizontal?: boolean;
      onScroll: (e: NativeSyntheticEvent<NativeScrollEvent>) => void;
      showsVerticalScrollIndicator: boolean;
      showsHorizontalScrollIndicator: boolean;
      scrollEventThrottle: number;
    };
  }) => {
    const result = React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        const displayName = getDisplayName(child);
        if (displayName === "ScrollView") {
          return React.cloneElement(child, opt);
        }
        if (displayName === "Track") return child;
      }
    });

    if (result.length < 2) {
      throw new Error(
        "Scrollbar.Wrapper must contain a ScrollView and a Track",
      );
    }

    return <React.Fragment>{result}</React.Fragment>;
  },
  (prev, next) => {
    return (
      prev.children === next.children &&
      prev.opt.horizontal === next.opt.horizontal &&
      prev.opt.onScroll === next.opt.onScroll &&
      prev.opt.showsVerticalScrollIndicator ===
        next.opt.showsVerticalScrollIndicator &&
      prev.opt.showsHorizontalScrollIndicator ===
        next.opt.showsHorizontalScrollIndicator &&
      prev.opt.scrollEventThrottle === next.opt.scrollEventThrottle
    );
  },
);

type ScrollViewWrapper = Omit<ViewProps, "children" | "flexDirection"> & {
  children: ReactElement[];
  horizontal?: boolean;
};

const ScrollViewWrapper: React.FC<ScrollViewWrapper> = ({
  children,
  horizontal = false,
  ...rest
}) => {
  const { onScroll, thumbRef, thumbLength, trackLength } =
    useScrollbar(horizontal);
  const direction = horizontal ? "column" : "row";

  return (
    <View flexDirection={direction} {...rest}>
      <ScrollbarWrapperContext.Provider
        value={{ horizontal, thumbLength, trackLength, thumbRef }}
      >
        <EnhancedChildren
          children={children}
          opt={{
            horizontal,
            onScroll,
            showsVerticalScrollIndicator: false,
            showsHorizontalScrollIndicator: false,
            scrollEventThrottle: 16,
          }}
        />
      </ScrollbarWrapperContext.Provider>
    </View>
  );
};

const Track: React.FC<
  Omit<ViewProps, "height" | "width"> & {
    thickness?: number | string;
  }
> = ({ children, thickness = 8, ...rest }) => {
  const { horizontal, trackLength } = useScrollbarContext();
  return (
    <View
      backgroundColor="$gray-300"
      position="relative"
      {...(!horizontal
        ? { width: thickness, height: trackLength }
        : { width: trackLength, height: thickness })}
      {...rest}
    >
      {children}
    </View>
  );
};

Track.displayName = "Track";

const Thumb: React.FC<
  Omit<ViewProps, "height" | "width" | "x" | "y"> & {
    thickness?: number | string;
  }
> = ({ thickness = "100%", ...rest }) => {
  const { thumbLength, horizontal, thumbRef } = useScrollbarContext();

  return (
    <View
      ref={thumbRef}
      position="absolute"
      backgroundColor="$green-500"
      {...(horizontal
        ? {
            width: thumbLength,
            height: thickness,
          }
        : {
            height: thumbLength,
            width: thickness,
          })}
      {...rest}
    />
  );
};

Thumb.displayName = "Thumb";

export const Scrollbar = {
  Track,
  Thumb,
  Wrapper: ScrollViewWrapper,
};
